import { ImageCell } from '@/components/shared/ImageCell';
import { cn } from '@/lib/utils';
import type { Attraction } from '@/config/objects/attractions';
import {
  GENERATION_TIMEOUT_MESSAGE,
  useAttractionImageGeneration,
} from '@/hooks/useAttractionImageGeneration';
import { Button } from '@startupquickstart/frontend';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { GenerateAttractionImagesDialog } from './GenerateAttractionImagesDialog';

/**
 * Query-key prefix for attraction list/detail queries. Invalidating this
 * prefix refetches the CrudListView list (`['Attraction', 'list', …]`), which
 * is how we pick up the newly generated featured image.
 */
const ATTRACTION_QUERY_KEY = 'Attraction';

interface AttractionImageCellProps {
  attraction?: Attraction;
  size?: 'sm' | 'lg';
}

/**
 * Editable attraction image cell with upload, delete, and inline AI generation.
 *
 * A "Generate with AI" affordance appears on hover; clicking it opens the
 * canonical {@link GenerateAttractionImagesDialog} (reference-photo confirmation
 * required). Once generation is kicked off the cell shows an in-progress spinner
 * and polls the list until the featured image changes (success) or a timeout
 * elapses (a dismissible error badge on the row).
 */
export function AttractionImageCell({ attraction, size = 'sm' }: AttractionImageCellProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const currentImageId = attraction?.featuredImage?.id ?? null;
  const { isGenerating, hasTimedOut, startGeneration, dismissTimeout } =
    useAttractionImageGeneration({
      currentImageId,
      invalidateKeys: [[ATTRACTION_QUERY_KEY]],
    });

  if (!attraction) return null;

  const openDialog = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger the cell's upload / row click.
    setDialogOpen(true);
  };

  const dismissError = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissTimeout();
  };

  const overlayActions = !isGenerating ? (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      onClick={openDialog}
      data-tour="attraction-generate-image"
      aria-label="Generate main photo with AI"
      title="Generate with AI"
      className="h-7 w-7 p-0"
    >
      <Sparkles className="h-3 w-3" />
    </Button>
  ) : null;

  const statusOverlay = isGenerating ? (
    <div
      role="status"
      aria-label="Generating photo"
      className="absolute inset-0 z-10 flex items-center justify-center rounded bg-black/60"
    >
      <Loader2 className="h-5 w-5 animate-spin text-white" />
    </div>
  ) : hasTimedOut ? (
    <button
      type="button"
      onClick={dismissError}
      aria-label={GENERATION_TIMEOUT_MESSAGE}
      title={GENERATION_TIMEOUT_MESSAGE}
      className={cn(
        'absolute inset-0 z-10 flex items-center justify-center rounded',
        'bg-destructive/70 text-white',
      )}
    >
      <AlertTriangle className="h-5 w-5" />
    </button>
  ) : null;

  return (
    <>
      <ImageCell
        item={attraction}
        endpoint="/attractions"
        entityName={ATTRACTION_QUERY_KEY}
        imageLabel="Attraction Image"
        getFeaturedImage={(item) => item.featuredImage}
        getItemId={(item) => item.id}
        size={size}
        overlayActions={overlayActions}
        statusOverlay={statusOverlay}
      />
      <GenerateAttractionImagesDialog
        attractionId={attraction.id}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onGenerated={startGeneration}
      />
    </>
  );
}
